import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useSnapCarousel as useLibrarySnapCarousel } from "react-snap-carousel"

// use-photo-carousel.ts is the intentional sibling: it does not use this hook
// because its guard targets a non-zero intended index (e.g. photo dialogs).

const RESET_CAP = 10
const EMPTY_SNAP_POINT_INDEXES = new Set<number>()

type SnapCarouselGoToOptions = {
  behavior?: ScrollBehavior
}

export type UseSnapCarouselOptions = {
  axis?: "x" | "y"
  initialPages?: number[][]
  contentKey?: unknown
  /**
   * How many items the carousel holds. The library measures its pages from the
   * DOM on mount and on resize only, so when the items change in place (a row
   * whose 10 cards become 4) its pages still point at cards that are gone, and
   * the next scroll throws "Expected HTMLElement". Pass the count and the pages
   * are re-measured whenever it changes.
   */
  itemCount?: number
  trackPages?: boolean
  guard?: boolean
}

function useCarouselScrollGuard(
  scrollEl: HTMLElement | null,
  contentKey: unknown,
  axis: "x" | "y"
) {
  const interactedRef = useRef(false)
  const resetCountRef = useRef(0)
  const scrollPos = axis === "x" ? "scrollLeft" : "scrollTop"
  const scrollAxis = axis === "x" ? "left" : "top"

  const markInteracted = () => {
    interactedRef.current = true
  }

  useLayoutEffect(() => {
    interactedRef.current = false
    resetCountRef.current = 0
  }, [scrollEl, contentKey])

  useLayoutEffect(() => {
    if (!scrollEl) return
    scrollEl.scrollTo({ [scrollAxis]: 0, behavior: "instant" })
  }, [scrollEl, contentKey, scrollAxis])

  useLayoutEffect(() => {
    if (!scrollEl) return

    const interactionTarget = scrollEl.parentElement ?? scrollEl

    const onInteract = () => {
      interactedRef.current = true
    }

    const interactionEvents = [
      "pointerdown",
      "touchstart",
      "wheel",
      "keydown",
      "focusin",
    ] as const

    for (const event of interactionEvents) {
      interactionTarget.addEventListener(event, onInteract, {
        capture: true,
        passive: true,
      })
    }

    const onScroll = () => {
      if (interactedRef.current) return
      if (scrollEl[scrollPos] === 0) return
      if (resetCountRef.current >= RESET_CAP) return

      resetCountRef.current += 1
      scrollEl.scrollTo({ [scrollAxis]: 0, behavior: "instant" })
    }

    scrollEl.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      for (const event of interactionEvents) {
        interactionTarget.removeEventListener(event, onInteract, {
          capture: true,
        })
      }
      scrollEl.removeEventListener("scroll", onScroll)
    }
  }, [scrollEl, scrollPos, scrollAxis])

  return { markInteracted }
}

export function useSnapCarousel({
  axis = "x",
  initialPages,
  contentKey,
  itemCount,
  trackPages = true,
  guard = true,
}: UseSnapCarouselOptions = {}) {
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null)
  const {
    scrollRef: libraryScrollRef,
    pages,
    activePageIndex,
    snapPointIndexes,
    hasPrevPage,
    hasNextPage,
    prev: libraryPrev,
    next: libraryNext,
    goTo: libraryGoTo,
    refresh,
  } = useLibrarySnapCarousel({ axis, initialPages })
  const { markInteracted } = useCarouselScrollGuard(
    guard ? scrollEl : null,
    contentKey,
    axis
  )

  const setScrollRef = (el: HTMLElement | null) => {
    setScrollEl(el)
    if (trackPages) {
      libraryScrollRef(el)
    }
  }

  useEffect(() => {
    if (trackPages && scrollEl) {
      libraryScrollRef(scrollEl)
    } else if (!trackPages) {
      libraryScrollRef(null)
    }
  }, [trackPages, scrollEl, libraryScrollRef])

  // Layout, not passive: the pages must be right before a scroll event can read them.
  useLayoutEffect(() => {
    if (trackPages && scrollEl) refresh()
  }, [itemCount, trackPages, scrollEl, refresh])

  const prev = (opts?: SnapCarouselGoToOptions) => {
    markInteracted()
    libraryPrev(opts)
  }

  const next = (opts?: SnapCarouselGoToOptions) => {
    markInteracted()
    libraryNext(opts)
  }

  const goTo = (pageIndex: number, opts?: SnapCarouselGoToOptions) => {
    markInteracted()
    libraryGoTo(pageIndex, opts)
  }

  return {
    scrollRef: setScrollRef,
    /** The scrolling element itself, for anything that needs it as an observer root. */
    scrollEl,
    pages: trackPages ? pages : [],
    activePageIndex: trackPages ? activePageIndex : 0,
    snapPointIndexes: trackPages ? snapPointIndexes : EMPTY_SNAP_POINT_INDEXES,
    hasPrevPage: trackPages ? hasPrevPage : false,
    hasNextPage: trackPages ? hasNextPage : false,
    prev,
    next,
    goTo,
    refresh,
  }
}
